import { c as _c } from "react/compiler-runtime";
import type { AnchorHTMLAttributes, HTMLAttributes } from 'react';
import type { CardSharedContentProps } from '@/app/components/card/shared/content';
import { CardSharedContent } from '@/app/components/card/shared/content';
import { CardSharedImage } from '@/app/components/card/shared/image';
import type { DateProps } from '@/app/components/card/shared/meta/date-full';
import type { ImageProps } from '@/app/components/media/image/types';
import { Link } from '@/app/components/navigation/link';
import { TagItem } from '@/app/components/tags';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';
import { stripHtmlTags } from '@/app/utils/format';
export type CardSharedTagType = {
  label: string;
  href: string;
};
export interface CardSharedProps extends CardSharedContentProps, Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  image: ImageProps;
  date?: DateProps | null;
  tag?: CardSharedTagType;
  href?: AnchorHTMLAttributes<HTMLAnchorElement>['href'];
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  dataGA?: DataGAProps;
  className?: string;
}
export const CardShared = t0 => {
  const $ = _c(55);
  let className;
  let dataGA;
  let date;
  let descriptionClassName;
  let href;
  let image;
  let rest;
  let t1;
  let t2;
  let tag;
  let target;
  let title;
  let titleClassName;
  let titleLineClamp;
  let untilText;
  if ($[0] !== t0) {
    ({
      image,
      href,
      target,
      date,
      tag,
      title,
      description: t1,
      dataGA,
      className,
      titleClassName,
      titleLineClamp,
      descriptionClassName,
      descriptionLineClamp: t2,
      untilText,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = dataGA;
    $[3] = date;
    $[4] = descriptionClassName;
    $[5] = href;
    $[6] = image;
    $[7] = rest;
    $[8] = t1;
    $[9] = t2;
    $[10] = tag;
    $[11] = target;
    $[12] = title;
    $[13] = titleClassName;
    $[14] = titleLineClamp;
    $[15] = untilText;
  } else {
    className = $[1];
    dataGA = $[2];
    date = $[3];
    descriptionClassName = $[4];
    href = $[5];
    image = $[6];
    rest = $[7];
    t1 = $[8];
    t2 = $[9];
    tag = $[10];
    target = $[11];
    title = $[12];
    titleClassName = $[13];
    titleLineClamp = $[14];
    untilText = $[15];
  }
  const description = t1 === undefined ? "" : t1;
  const descriptionLineClamp = t2 === undefined ? true : t2;
  const isHoverable = !!href || !!rest.onClick;
  const t3 = !!rest.onClick;
  const t4 = !!href;
  let t5;
  if ($[16] !== className || $[17] !== isHoverable || $[18] !== t3 || $[19] !== t4) {
    t5 = cn("text-body-2 pointer-coarse:bg-silver-100", {
      "group hover:bg-silver-100 transition-bg-color rounded-sm p-2 md:rounded-md": isHoverable,
      "cursor-pointer": t3,
      relative: t4
    }, className);
    $[16] = className;
    $[17] = isHoverable;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== isHoverable) {
    t6 = cn({
      "rounded-2xs": isHoverable
    });
    $[21] = isHoverable;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== image || $[24] !== t6) {
    t7 = {
      ...image,
      className: t6
    };
    $[23] = image;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isHoverable || $[27] !== t7) {
    t8 = <CardSharedImage image={t7} isHoverable={isHoverable} />;
    $[26] = isHoverable;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== dataGA || $[30] !== href || $[31] !== target || $[32] !== title) {
    t9 = !!href && <Link href={href} target={target} aria-label={stripHtmlTags(title)} className="absolute inset-0 block" {...createDataGA({
      click: true,
      url: href,
      ...dataGA
    })} {...createDataAttributes({
      test: "card-link"
    })} />;
    $[29] = dataGA;
    $[30] = href;
    $[31] = target;
    $[32] = title;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== dataGA?.eventName || $[35] !== tag) {
    t10 = !!tag && <TagItem id={tag.href} href={tag.href} label={tag.label} tagCustomStyled={{
      base: "hover:bg-gray-800 hover:text-white hover:before:border-gray-800 focus-visible:bg-gray-800 focus-visible:text-white focus-visible:before:border-gray-800 active:bg-gray-800 active:text-white active:before:border-gray-800"
    }} {...createDataGA({
      click: true,
      eventName: dataGA?.eventName ? `${dataGA?.eventName}-tag` : "card-tag",
      url: tag.href,
      text: tag.label,
      value: tag.href
    })} />;
    $[34] = dataGA?.eventName;
    $[35] = tag;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== date || $[38] !== description || $[39] !== descriptionClassName || $[40] !== descriptionLineClamp || $[41] !== title || $[42] !== titleClassName || $[43] !== titleLineClamp || $[44] !== untilText) {
    t11 = <CardSharedContent title={title} description={description} date={date} titleClassName={titleClassName} titleLineClamp={titleLineClamp} descriptionClassName={descriptionClassName} descriptionLineClamp={descriptionLineClamp} untilText={untilText} />;
    $[37] = date;
    $[38] = description;
    $[39] = descriptionClassName;
    $[40] = descriptionLineClamp;
    $[41] = title;
    $[42] = titleClassName;
    $[43] = titleLineClamp;
    $[44] = untilText;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t10 || $[47] !== t11) {
    t12 = <div className="space-y-2 px-2 xl:space-y-4">{t10}{t11}</div>;
    $[46] = t10;
    $[47] = t11;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== rest || $[50] !== t12 || $[51] !== t5 || $[52] !== t8 || $[53] !== t9) {
    t13 = <article className={t5} {...rest}>{t8}{t9}{t12}</article>;
    $[49] = rest;
    $[50] = t12;
    $[51] = t5;
    $[52] = t8;
    $[53] = t9;
    $[54] = t13;
  } else {
    t13 = $[54];
  }
  return t13;
};